/* External */
import mapKeys from 'lodash/mapKeys';

/* Ott-common */
import { OrbisResourceType } from '@dtvgo/constants';

/* Utils */
import {
  EntityName,
  PROVIDER,
  BLIM_PICTURES_TYPE,
  Source,
  SectionId,
  ContentSourceType,
  OrbisResourceTypeByBlimResourceType,
  OrbisLiveResourceTypeByBlimResourceType,
} from './constants';

export function orbisResourceTypeToEntityName(orbisResourceType) {
  switch (orbisResourceType) {
    case OrbisResourceType.ASSET:
      return EntityName.ASSET;
    case OrbisResourceType.BANNER:
      return EntityName.BANNER;
    case OrbisResourceType.STATION:
      return EntityName.STATION;
    case OrbisResourceType.SHOW:
      return EntityName.SHOW;
    case OrbisResourceType.SEASON:
      return EntityName.SEASON;
    case OrbisResourceType.VOD_MOVIE:
    case OrbisResourceType.VOD_EPISODE:
    case OrbisResourceType.VOD_EVENT:
    case OrbisResourceType.VOD_COMPETITION:
    case OrbisResourceType.VOD_TEAM_COMPETITION:
      return EntityName.VOD_CONTENT;
    case OrbisResourceType.LIVE_MOVIE:
    case OrbisResourceType.LIVE_EPISODE:
    case OrbisResourceType.LIVE_EVENT:
    case OrbisResourceType.LIVE_COMPETITION:
    case OrbisResourceType.LIVE_TEAM_COMPETITION:
      return EntityName.LIVE_CONTENT;
    default:
      return orbisResourceType;
  }
}

export function schemaAttribute(entityValue) {
  return orbisResourceTypeToEntityName(entityValue.resourceType);
}

export function normalizeIdFields(data) {
  return mapKeys(data, (value, key) => key.replace(/ID/, 'Id'));
}

function createResourceTypeNormalizer(isLive) {
  const mapper = isLive
    ? OrbisLiveResourceTypeByBlimResourceType
    : OrbisResourceTypeByBlimResourceType;

  return (entity) => {
    const {
      __rtAlreadyNormalized,
      resourceType,
      type,
      programType,
      sourceProvider,
      ...rest
    } = entity;
    if (sourceProvider === PROVIDER.BLIM && !__rtAlreadyNormalized) {
      return {
        resourceType: mapper[resourceType || programType || type] || mapper.default,
        sourceProvider,
        __rtAlreadyNormalized: true,
        ...rest,
      };
    }

    return entity;
  };
}

export const normalizeResourceType = createResourceTypeNormalizer(false);
export const normalizeLiveResourceType = createResourceTypeNormalizer(true);

export function normalizeSourceProvider({ sourceProvider, ...data }) {
  return {
    sourceProvider: sourceProvider || PROVIDER.ORBIS,
    ...data,
  };
}

export function normalizeDuration({ sourceProvider, duration, ...data }) {
  return {
    duration: sourceProvider === PROVIDER.BLIM ? (duration * 60) : duration,
    sourceProvider,
    ...data,
  };
}

export function normalizePictureId({
  pictures,
  pictureId,
  sourceProvider,
  ...data
}) {
  let pictureIdBlim;
  if (pictures && sourceProvider === PROVIDER.BLIM) {
    pictureIdBlim = pictures.map(({ url }) => url);
  }

  return {
    pictureId: pictureIdBlim || pictureId,
    sourceProvider,
    ...data,
  };
}

export function normalizePictures({ pictures, ...data }) {
  const blimPictures = {};
  const isArrayOfPictures = Array.isArray(pictures);
  if (pictures && isArrayOfPictures) {
    pictures.map(({ type, url }) => {
      switch (type) {
        case BLIM_PICTURES_TYPE.BACKGROUND:
          blimPictures['16x9'] = url;
          blimPictures['4x3'] = url;
          break;
        case BLIM_PICTURES_TYPE.COVER_VERTICAL:
          blimPictures['2x3'] = url;
          break;
        default:
          return null;
      }

      return blimPictures;
    });
  }

  return {
    pictures: (pictures && isArrayOfPictures) ? blimPictures : pictures,
    ...data,
  };
}

export function normalizeBlimSection(currentSection) {
  const {
    title, carouselId, id, contents, sourceProvider, type,
  } = currentSection;
  return {
    id: id || carouselId,
    name: title,
    items: contents
      ?.filter(({ id: itemId }) => !!itemId)
      ?.map((item) => normalizeResourceType({
        ...item,
        sourceProvider,
      })),
    sourceProvider,
    type,
  };
}

export function normalizeOrbisSection(currentSection) {
  const {
    subsections: [subsection = {}] = [],
    sectionId,
    label,
    sourceProvider,
    contents,
  } = currentSection;
  const { items, blockLabel } = subsection;

  return {
    id: sectionId,
    name: blockLabel || label,
    items: (items || contents)?.filter(({ id }) => !!id),
    sourceProvider,
  };
}

export function normalizeTAMostWatched(currentSection) {
  const {
    id, items, label, sourceProvider,
  } = currentSection;

  return {
    id,
    name: label,
    items: items
      ?.filter(({ id: itemId }) => !!itemId)
      ?.map(({ resourceType, ...rest }) => ({
        resourceType: resourceType || OrbisResourceType.STATION,
        ...rest,
      }))
      ?.sort((itemA, itemB) => (itemB.rank - itemA.rank)),
    sourceProvider,
  };
}

export function normalizeAssetId({
  assetId,
  assetIds,
  assetID,
  assetIDs,
  ...data
}) {
  return {
    assetId: assetId || (assetIds && assetIds[0]) || assetID || (assetIDs && assetIDs[0]),
    ...data,
  };
}

export function normalizeEpisodeAttributes({
  seasonNumber,
  season,
  episodeNumber,
  episode,
  ...data
}) {
  return {
    seasonNumber: seasonNumber || season,
    episodeNumber: episodeNumber || episode,
    ...data,
  };
}

export function normalizeReleaseYear({
  releaseYear,
  realeaseYear,
  ...data
}) {
  return {
    releaseYear: releaseYear || realeaseYear,
    ...data,
  };
}

export function normalizeBlimEpisodes(entity) {
  const {
    id,
    showId,
    episodes,
    sourceProvider,
    ...data
  } = entity;
  if (sourceProvider === PROVIDER.BLIM) {
    return {
      id,
      showId,
      episodes: episodes?.map((episode) => ({
        showId,
        seasonId: id,
        sourceProvider,
        resourceType: OrbisResourceType.VOD_EPISODE,
        __rtAlreadyNormalized: true,
        ...episode,
      })),
      sourceProvider,
      ...data,
    };
  }

  return entity;
}

export function normalizeEpisodeCount({
  episodeCount,
  episodes,
  ...data
}) {
  return {
    episodeCount: episodeCount || episodes?.length,
    episodes,
    ...data,
  };
}

export function normalizeSeasonCount({
  seasons,
  seasonCount,
  ...data
}) {
  return {
    seasonCount: seasonCount || seasons,
    ...data,
  };
}

export function sortSeasonsList({
  seasonsList,
  seasonList,
  ...data
}) {
  const listOfSeasons = seasonsList || seasonList;
  if (listOfSeasons) {
    listOfSeasons.sort((seasonA, seasonB) => seasonA.seasonNumber - seasonB.seasonNumber);
  }

  return {
    seasonsList: listOfSeasons,
    ...data,
  };
}

export function sortEpisodes({
  episodes,
  sourceProvider,
  ...data
}) {
  return {
    episodes: episodes && episodes.sort(
      (episodeA, episodeB) => (
        (episodeA.episodeNumber || episodeA.episode) - (episodeB.episodeNumber || episodeB.episode)
      ),
    ),
    sourceProvider,
    ...data,
  };
}

export function normalizeGenres({
  genres,
  ...data
}) {
  let normalizedGenres = genres;
  if (genres && typeof genres[0] === 'string') {
    normalizedGenres = genres.map((name) => ({ name }));
  } else if (genres) {
    normalizedGenres = genres.map(({ name, title, id }) => (
      {
        name: name || title,
        id,
      }
    ));
  }
  return {
    genres: normalizedGenres,
    ...data,
  };
}

export function normalizeSeasons(currentShow) {
  const { seasonList, id, sourceProvider } = currentShow;

  if (sourceProvider === PROVIDER.BLIM) {
    const seasonBlimList = seasonList
      ?.filter((seasonNumber) => !!seasonNumber)
      ?.map((seasonNumber) => ({
        id: `${id}_${seasonNumber}`,
        showId: id,
        seasonNumber,
        sourceProvider,
      }));
    return {
      ...currentShow,
      seasonsList: seasonBlimList,
    };
  }

  return currentShow;
}

function convertBlimVideosAttrToUrlsObject(videos) {
  return videos.reduce((result, { type, path }) => ({
    ...result,
    [mapVideoManifestType(type)]: {
      primary: path,
    },
  }), {});
}

export function normalizeBlimAsset(asset) {
  const {
    sourceProvider, videos, resourceType, ...data
  } = asset;

  if (sourceProvider === PROVIDER.BLIM) {
    const urlsObject = convertBlimVideosAttrToUrlsObject(videos);
    if (resourceType === OrbisResourceType.STATION) {
      return {
        sourceProvider,
        resourceType,
        liveURLs: urlsObject,
        ...data,
      };
    }
    return {
      sourceProvider,
      resourceType,
      vodURLs: urlsObject,
      ...data,
    };
  }

  return asset;
}

export function mapVideoManifestType(type) {
  switch (type) {
    case 'mpd': return 'dash';
    case 'hls': return 'hls';
    default: return type;
  }
}

export function normalizeSearchResult(result) {
  if (result.source === Source.Blim) {
    const resourceTypeNormalizer = result.contentSourceId === ContentSourceType.BlimLive
      ? normalizeLiveResourceType
      : normalizeResourceType;

    return resourceTypeNormalizer({
      ...result,
      sourceProvider: PROVIDER.BLIM,
      duration: result.duration / 60,
    });
  }

  if (result.source === Source.Orbis) {
    return {
      ...result,
      sourceProvider: PROVIDER.ORBIS,
    };
  }

  return result;
}

export function normalizeSearchResults(results) {
  return results.map(normalizeSearchResult);
}

export function normalizeBlimStation({ streams, images = [], ...data }) {
  return {
    videos: streams,
    entitled: true,
    resourceType: OrbisResourceType.STATION,
    picture: images[0]?.url,
    ...data,
  };
}

export function normalizeName({ name, title, ...data }) {
  return {
    name: name || title,
    ...data,
  };
}

export function normalizeBlimLiveContentId(liveContent) {
  if (liveContent.sourceProvider === PROVIDER.BLIM) {
    return {
      ...liveContent,
      id: `${liveContent.id}-${new Date(liveContent.startTime).valueOf()}`,
    };
  }

  return liveContent;
}

export function sortFeaturedSection(page) {
  const featuredTA = page.sections.find((section) => section.label === SectionId.FEATURED_CONTENT);
  const [firstSection, ...nonFeature] = page.sections.filter(
    (section) => (
      section.label !== SectionId.FEATURED_CONTENT
    ),
  );

  return {
    ...page,
    sections: [
      featuredTA || firstSection,
      ...nonFeature,
    ],
  };
}
